import React, { InputHTMLAttributes, forwardRef } from "react";
import { AlertCircle } from "lucide-react";
import { analytics } from "../../utils/analytics";
import { useLocation } from "react-router-dom";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  trackingId?: string;
  page?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helpText,
      fullWidth = false,
      icon,
      className = "",
      trackingId,
      page,
      onChange,
      ...props
    },
    ref
  ) => {
    const location = useLocation();

    // Auto-detect page context from current route if not provided
    const getPageContext = (): string => {
      if (page) return page;

      const pathname = location.pathname;

      // Map routes to page names
      if (pathname === "/" || pathname === "/app") return "app_start";
      if (pathname.includes("/contact")) return "contact_form";
      if (pathname.includes("/measurement")) return "measurement_form";
      if (pathname.includes("/upload") || pathname.includes("/file"))
        return "file_upload";
      if (pathname.includes("/price") || pathname.includes("/estimation"))
        return "price_estimation";
      if (pathname.includes("/booking") || pathname.includes("/calendar"))
        return "booking_calendar";
      if (pathname.includes("/confirmation")) return "confirmation";
      if (pathname.includes("/dashboard")) return "dashboard";
      if (pathname.includes("/admin")) return "admin_panel";

      // Fallback: extract page name from pathname
      const segments = pathname.split("/").filter(Boolean);
      return segments.length > 0 ? segments[segments.length - 1] : "app_start";
    };

    // Auto-generate input ID if not provided
    const getInputId = (): string => {
      if (trackingId) return trackingId;
      if (props.id) return props.id;
      if (props.name) return props.name;
      if (label)
        return label
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "");
      return "input_field";
    };
    const baseClasses =
      "px-3 py-2 border rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

    const errorClasses = error
      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:border-amber-500 focus:ring-amber-500 dark:border-gray-600 dark:focus:border-amber-400";

    const backgroundClasses =
      "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100";

    const widthClass = fullWidth ? "w-full" : "";

    const inputClasses =
      `${baseClasses} ${errorClasses} ${backgroundClasses} ${widthClass} ${
        icon ? "pl-10" : ""
      } ${className}`.trim();

    // Handle input changes and track analytics
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputId = getInputId();
      const pageContext = getPageContext();

      // Track after a slight delay to avoid tracking every keystroke
      if (e.target.value.length % 5 === 0 || e.target.value.length === 0) {
        analytics.trackInputChange(inputId, pageContext, e.target.value);
      }

      // Call the original onChange handler if provided
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <div className={fullWidth ? "w-full" : ""}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
          </label>
        )}

        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <div className="text-gray-400 dark:text-gray-500">{icon}</div>
            </div>
          )}

          <input
            ref={ref}
            className={inputClasses}
            onChange={handleChange}
            {...props}
          />
        </div>

        {error && (
          <div className="mt-1 flex items-center text-sm text-red-600">
            <AlertCircle className="w-4 h-4 mr-1" />
            {error}
          </div>
        )}

        {helpText && !error && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
